import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { Textarea } from './textarea';

const REPORT_REASONS = [
  'Spam or scam',
  'Nudity or sexual activity',
  'Hate speech or symbols',
  'Violence or dangerous content',
  'Harassment or bullying',
  'Inappropriate content',
  'Other',
] as const;

interface ReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentLabel: string;
  isSubmitting: boolean;
  onSubmit: (reasons: string[], details: string) => void;
}

export function ReportDialog({
  open,
  onOpenChange,
  contentLabel,
  isSubmitting,
  onSubmit,
}: ReportDialogProps) {
  const [reasons, setReasons] = React.useState<string[]>([]);
  const [details, setDetails] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setReasons([]);
      setDetails('');
    }
  }, [open]);

  const handleReasonChange = (reason: string, checked: boolean) => {
    setReasons(prev => (checked ? [...prev, reason] : prev.filter(r => r !== reason)));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* max-h + an inner scroll region: react-remove-scroll only lets a touch gesture
          through when its target has a scrollable ancestor (handleScroll.js:18-36). With
          DialogContent capped at nothing and overflow-hidden, the only qualifying element
          was the textarea (hard-coded as always-scrollable there), so with the keyboard up
          the reasons list could not be reached. overflow-hidden stays for the rounded
          corners; the scrolling happens in the middle section. Design doc §5 item 3. */}
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden rounded-2xl flex flex-col max-h-[calc(100dvh-4rem)]">
        <DialogHeader className="p-6 pb-4 shrink-0">
          <DialogTitle className="text-xl font-semibold">Report {contentLabel}</DialogTitle>
          <DialogDescription className="text-gray-600">
            Help us understand what's happening
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-4 space-y-4 min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-900">
              Why are you reporting this {contentLabel}? (Select all that apply) *
            </label>
            <div className="space-y-2">
              {REPORT_REASONS.map((reason, index) => (
                <div key={reason} className="flex items-center space-x-3">
                  <Checkbox
                    id={`report-reason-${index}`}
                    checked={reasons.includes(reason)}
                    onCheckedChange={checked => {
                      handleReasonChange(reason, checked as boolean);
                    }}
                    className="data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                  />
                  <label
                    htmlFor={`report-reason-${index}`}
                    className="text-sm text-gray-900 cursor-pointer flex-1"
                  >
                    {reason}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label
              className="text-sm font-medium text-gray-900"
              htmlFor="report-additional-details"
            >
              Additional details (optional)
            </label>
            <Textarea
              id="report-additional-details"
              value={details}
              onChange={e => {
                setDetails(e.target.value);
              }}
              placeholder="Please provide any additional context that would help us understand the issue..."
              className="min-h-[80px] resize-none"
              maxLength={500}
              disabled={isSubmitting}
            />
            <div className="flex justify-end">
              <span className="text-xs text-gray-500">{details.length}/500</span>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 bg-gray-50 [--mb-surface:var(--color-gray-50)] border-t border-gray-100 gap-3 shrink-0">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={isSubmitting}
            className="px-6"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onSubmit(reasons, details.trim());
            }}
            disabled={reasons.length === 0 || isSubmitting}
            className="bg-red-600 hover:bg-red-700 text-white px-6"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Submitting...
              </>
            ) : (
              'Submit Report'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
